import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Drawer, IconButton } from "@mui/material";
import { Image as ImageIcon, X } from "react-feather";
import { addToCart, belowMinimum, CART_EVENT, CART_OPEN_EVENT, checkoutPath, formatPrice, getCart, isCustomer, removeFromCart } from "../shopApi";
import MinOrderNotice from "./MinOrderNotice";
import styles from "../shop.module.css";

/**
 * Slide-in cart from the right. Mount one per page; openCartDrawer() (shopApi) opens it,
 * and it follows every cart change on the page. shopPath: "/shop" or "/customer/shop".
 */
const CartDrawer = ({ shopPath, cartPath }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [cart, setCart] = useState(null);
  const [busyLine, setBusyLine] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    const onOpen = () => {
      setOpen(true);
      getCart().then((res) => setCart(res.data)).catch(() => { });
    };
    const onChange = (e) => setCart(e.detail);
    window.addEventListener(CART_OPEN_EVENT, onOpen);
    window.addEventListener(CART_EVENT, onChange);
    return () => {
      window.removeEventListener(CART_OPEN_EVENT, onOpen);
      window.removeEventListener(CART_EVENT, onChange);
    };
  }, []);

  const run = (lineId, request) => {
    setBusyLine(lineId);
    setError("");
    request.then(() => setBusyLine(0)).catch((e) => { setBusyLine(0); setError(e.message); });
  };

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const items = cart?.items || [];
  const blocked = items.some((i) => i.unavailable);

  return (<Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ className: styles.CartDrawer }}>
    <div className={styles.DrawerHead}>
      <strong>Your cart{cart?.count ? ` (${cart.count})` : ""}</strong>
      <IconButton onClick={() => setOpen(false)} aria-label="Close cart"><X size={20} /></IconButton>
    </div>

    <div className={styles.DrawerBody}>
      {items.length === 0 && (<div className={styles.Empty}>
        <p>Your cart is empty.</p>
        <Button className={styles.PrimaryBU} onClick={() => go(shopPath)} style={{ marginTop: 12 }}>Continue shopping</Button>
      </div>)}

      {items.map((line) => (<div className={styles.DrawerLine} key={line.id}>
        {line.product.image ? <img src={line.product.image} alt="" className={styles.DrawerThumb} /> : <span className={`${styles.DrawerThumb} ${styles.NoImage}`}><ImageIcon size={18} /></span>}
        <div className={styles.DrawerLineInfo}>
          <Link to={`${shopPath}/${line.product_id}`} className={styles.LineTitle} onClick={() => setOpen(false)}>{line.product.title}</Link>
          <div className={styles.LineMeta}>
            {line.product.on_sale && <s className={styles.WasPrice}>{formatPrice(line.product.compare_at_price)}</s>}
            {formatPrice(line.product.price)}
          </div>
          {line.unavailable && <div className={styles.LineWarn}>{line.product.in_stock ? `Only ${line.product.max_qty} left` : "Sold out"}</div>}
          <div className={styles.DrawerLineActions}>
            <div className={styles.Qty}>
              <button type="button" disabled={busyLine === line.id} onClick={() => run(line.id, line.quantity <= 1 ? removeFromCart(line.id) : addToCart(line.product_id, line.quantity - 1, true))} aria-label="Fewer">−</button>
              <span>{line.quantity}</span>
              <button type="button" disabled={busyLine === line.id || (line.product.max_qty !== null && line.quantity >= line.product.max_qty)} onClick={() => run(line.id, addToCart(line.product_id, line.quantity + 1, true))} aria-label="More">+</button>
            </div>
            <button type="button" className={styles.RemoveBU} disabled={busyLine === line.id} onClick={() => run(line.id, removeFromCart(line.id))}>Remove</button>
          </div>
        </div>
        <strong>{formatPrice(line.line_total)}</strong>
      </div>))}
      {error && <span className={styles.Error}>{error}</span>}
    </div>

    {items.length > 0 && (<div className={styles.DrawerFoot}>
      <div className={`${styles.SumRow} ${styles.SumTotal}`}><span>Subtotal</span><span>{formatPrice(cart.subtotal)}</span></div>
      <span className={styles.Hint}>Delivery is worked out at checkout.</span>
      <MinOrderNotice cart={cart} />
      <Button fullWidth size="large" className={styles.PrimaryBU} disabled={blocked || belowMinimum(cart)} onClick={() => go(checkoutPath())}>Checkout</Button>
      <Button fullWidth className={styles.SecondaryBU} onClick={() => go(cartPath)}>View cart</Button>
      {!isCustomer() && <span className={styles.Hint} style={{ textAlign: "center" }}>Sign in or create an account to check out. Your cart is saved.</span>}
    </div>)}
  </Drawer>);
};

export default CartDrawer;
